<!-- TodaysFoods.vue -->
<template>
	<div class="todays-foods p-2">
		<!-- Loading state -->
		<glass-card v-if="loading" class="text-center py-4 meal-plan p-4">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
		</glass-card>

		<!-- No data state -->
		<glass-card v-else-if="!groups.length" class="text-center meal-plan p-4">
			<p>No meals recorded today</p>
		</glass-card>

		<!-- Groups -->
		<div v-else class="meal-groups">
			<glass-card
				v-for="group in groups"
				:key="group.type"
				class="meal-group mb-4">
				<div
					class="group-header d-flex align-items-center justify-content-between p-3">
					<h3 class="mb-0 text-capitalize">{{ group.type }}</h3>
					<div class="d-flex align-items-center">
						<rating-gauge
							class="me-3"
							width="60"
							:rating="group.average_rating"
							v-if="group.average_rating" />
					</div>
				</div>

				<!-- Nutritional Summary -->
				<div class="nutrition-summary">
					<div class="nutrition-row">
						<div class="nutrition-card">
							<div class="nutrition-icon">
								<i class="bi bi-fire"></i> Calories
							</div>
							<div class="nutrition-value">
								{{ group.nutritional_summary.calories }}
							</div>
						</div>
						<div class="nutrition-card">
							<div class="nutrition-icon">
								<i class="bi bi-egg-fried"></i> Protein
							</div>
							<div class="nutrition-value">
								{{ group.nutritional_summary.protein }}g
							</div>
						</div>
						<div class="nutrition-card">
							<div class="nutrition-icon">
								<i class="bi bi-circle-square"></i> Carbs
							</div>
							<div class="nutrition-value">
								{{ group.nutritional_summary.carbs }}g
							</div>
						</div>
						<div class="nutrition-card">
							<div class="nutrition-icon">
								<i class="bi bi-droplet-fill"></i> Fats
							</div>
							<div class="nutrition-value">
								{{ group.nutritional_summary.fats }}g
							</div>
						</div>
					</div>
					<div class="nutrition-row">
						<div class="nutrition-card">
							<div class="nutrition-icon">
								<i class="bi bi-cup-straw"></i> Sugars
							</div>
							<div class="nutrition-value">
								{{ group.nutritional_summary.sugars }}g
							</div>
						</div>
						<div class="nutrition-card">
							<div class="nutrition-icon">
								<i class="bi bi-flower1"></i> Fiber
							</div>
							<div class="nutrition-value">
								{{ group.nutritional_summary.fiber }}g
							</div>
						</div>
						<div class="nutrition-card">
							<div class="nutrition-icon">
								<i class="bi bi-water"></i> Sodium
							</div>
							<div class="nutrition-value">
								{{ group.nutritional_summary.sodium }}mg
							</div>
						</div>
					</div>
				</div>

				<!-- Food Items -->
				<div class="food-items">
					<div
						v-for="item in group.items"
						:key="item.id"
						class="food-item p-3"
						:style="{ backgroundImage: `url(${getImageSource(item.img)})` }"
						@click="openFoodDialog(item)">
						<div class="food-item-overlay"></div>
						<div class="food-item-content">
							<div class="food-details">
								<h4 class="food-title mb-2">{{ getName(item) }}</h4>
								<div class="d-flex align-items-center mb-2">
									<span
										v-if="item.cook_type"
										class="badge me-2"
										:class="[getCookTypeColor(item.cook_type)]">
										{{ item.cook_type }}
									</span>
									<span v-if="item.restaurant_name" class="restaurant-name">
										<i class="bi bi-geo-alt-fill me-1"></i
										>{{ item.restaurant_name }}
									</span>
								</div>
								<p
									v-if="item.entry.image_description"
									class="food-description mb-2">
									{{ getDescription(item) }}
								</p>
							</div>
							<div class="food-rating">
								<rating-gauge
									width="60"
									:rating="item.rating"
									v-if="item.rating" />
							</div>
							<allergy :meal="item" :show-text="false" />
						</div>
					</div>
				</div>
			</glass-card>
		</div>

		<!-- Food Detail Modal -->
		<food-detail-modal
			:meal="selectedFood"
			:show="showFDM"
			@close="closeModal" />
	</div>
</template>

<script setup>
	import { ref, onMounted, watch, onBeforeMount, onBeforeUnmount } from "vue"
	import GlassCard from "./GlassCard.vue"
	import { fetchWithAuth } from "@/utils/api-client"
	import RatingGauge from "./RatingGauge.vue"
	// import ImageLoader from "./ImageLoader.vue"
	import FoodDetailModal from "./FoodDetailModal.vue"
	import {
		getImageSource,
		getName,
		getCookTypeColor,
		getDescription,
	} from "@/utils/mealLib"
	// import {
	// 	lastMealUpdated,
	// 	lastMealDeleted,
	// 	lastMealAdded,
	// } from "@/utils/mealBus"
	import EventBus from "@/utils/eventBus"
	import { isOnline } from "@/utils/networkBus"
	import Allergy from "./Allergy.vue"

	const loading = ref(true)
	const groups = ref([])
	const selectedFood = ref(null)
	const showFDM = ref(false)

	const closeModal = () => {
		selectedFood.value = null
		showFDM.value = false
	}

	const fetchTodaysFoods = async () => {
		if (!isOnline.value) return

		loading.value = true
		try {
			const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
			const response = await fetchWithAuth("/api/todays-meal", {
				headers: {
					"User-Timezone": tz,
				},
			})
			const data = await response.json()

			groups.value = data.groups
		} catch (error) {
			console.error("Error fetching today's foods:", error)
		} finally {
			loading.value = false
		}
	}

	const openFoodDialog = (food) => {
		selectedFood.value = food
		showFDM.value = true
	}

	// Initial fetch
	onMounted(() => {
		fetchTodaysFoods()
	})

	// // Watch for updates
	// // watch([lastMealUpdated, lastMealDeleted, lastMealAdded], (v) => {
	// // 	fetchTodaysFoods()
	// // })

	// watch(lastMealUpdated, (v) => {
	// 	if (!v) return
	// 	if (selectedFood.value && selectedFood.value.id === v.id) {
	// 		selectedFood.value = v
	// 	}
	// 	fetchTodaysFoods()
	// })

	// watch([lastMealAdded, lastMealDeleted], (v) => {
	// 	if (!v) return
	// 	fetchTodaysFoods()
	// })

	let offMealUpdated = null
	let offMealDeleted = null
	let offMealAdded = null

	onBeforeMount(() => {
		offMealUpdated = EventBus.on("meal-updated", (meal) => {
			if (selectedFood.value && selectedFood.value.id === meal.id) {
				selectedFood.value = meal
			}
			fetchTodaysFoods()
		})

		offMealDeleted = EventBus.on("meal-deleted", (meal) => {
			if (!meal) return
			fetchTodaysFoods()
		})

		offMealAdded = EventBus.on("meal-added", (meal) => {
			if (!meal) return
			fetchTodaysFoods()
		})
	})

	onBeforeUnmount(() => {
		if (offMealUpdated) offMealUpdated()
		if (offMealDeleted) offMealDeleted()
		if (offMealAdded) offMealAdded()
	})
</script>

<style lang="less" scoped>
	@import "../assets/colors.less";

	.todays-foods {
		// max-width: 400px;
		// width: 100%;
		display: flex;
		justify-content: center;

		.meal-group {
			max-width: 400px;
			width: 100%;
			border-radius: 8px;
			box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
			overflow: hidden;

			.group-header {
				background: #007000;
				color: white;
			}
		}

		.nutrition-summary {
			// background: white;
			padding: 0;
		}

		.nutrition-row {
			display: flex;
			border-bottom: 1px solid #f0f0f0;

			&:last-child {
				border-bottom: none;
			}
		}

		.nutrition-card {
			flex: 1;
			text-align: center;
			padding: 15px 5px;
			// background: white;
			border-right: 1px solid #f0f0f0;

			&:last-child {
				border-right: none;
			}
		}

		.nutrition-icon {
			color: #ff6400;
			font-weight: bold;
			font-size: 0.9rem;
			margin-bottom: 5px;

			i {
				margin-right: 5px;
			}
		}

		.nutrition-value {
			font-size: 1.2rem;
			font-weight: bold;
			color: #ff6400;
		}

		.food-items {
			.food-item {
				cursor: pointer;
				transition: background-color 0.2s;
				position: relative;
				min-height: 150px;
				background-size: cover;
				background-position: center;
				color: white;

				&:last-child {
					border-bottom: none;
				}

				.food-item-overlay {
					position: absolute;
					top: 0;
					left: 0;
					right: 0;
					bottom: 0;
					background: rgba(0, 0, 0, 0.6);
					z-index: 1;
				}

				.food-item-content {
					position: relative;
					z-index: 2;
					display: flex;
					justify-content: space-between;
					height: 100%;
				}

				.food-title {
					font-size: 1.2rem;
					margin-bottom: 0.5rem;
					color: white;
				}

				.restaurant-name {
					color: #e0e0e0;
					font-size: 0.9rem;
				}

				.food-description {
					font-size: 0.9rem;
					color: #e0e0e0;
					margin: 0;
					display: -webkit-box;
					-webkit-line-clamp: 2;
					-webkit-box-orient: vertical;
					overflow: hidden;
				}

				/* Allergy Badge Styles */
				.allergy-container {
					position: absolute;
					bottom: 10px;
					right: 10px;
				}
			}
		}
	}
</style>
